export const TIME_WINDOW = 100;

export const TriageStatus = {
  NEW: 0,
  DUPLICATE: 1,
  IGNORED: 2,
  THROTTLED: 3,
};

type valueof<T> = T[keyof T];
export type TriageStatusType = valueof<typeof TriageStatus>;

export type TriagedMessage = {
  timestamp: number;
  status: TriageStatusType;
};

const triagedMap = new Map<string, TriagedMessage>();

/**
 * Triage a message before sending it to Sentry.
 *
 * The goal of this function is avoid bursts of errors that can bust our
 * monthly quota on Sentry.
 *
 * The triage algorithm tries to surface the nature of the burst:
 *   1. a burst in a short period of time
 *   2. a burst in a long period of time
 *
 * 1. will be surfaced as 2 messages in Sentry. The first one will remain
 * untouched, while the second one will be marked a duplicate. The following
 * messages will be ignored.
 *
 * 2. will be surfaced as 1. during the `TIME_WINDOW`. After this period
 * the message will be throttled at the edge of the time window to surface and
 * marked as throttled.
 */
const triageMessage = (message: string): TriageStatusType => {
  const now = Date.now();
  const entry = triagedMap.get(message);

  if (!entry) {
    triagedMap.set(message, {
      timestamp: now,
      status: TriageStatus.NEW,
    });

    return TriageStatus.NEW;
  }

  const shouldDedupe = now - entry.timestamp < TIME_WINDOW;
  const wasNew = entry.status === TriageStatus.NEW;

  const status = shouldDedupe
    ? wasNew
      ? TriageStatus.DUPLICATE
      : TriageStatus.IGNORED
    : TriageStatus.THROTTLED;

  if (status === TriageStatus.THROTTLED) {
    entry.timestamp = now;
  }
  entry.status = status;

  return status;
};

export default triageMessage;
